import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';

const DEFAULT_SES_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'sa-east-1',
  'eu-west-1',
  'eu-west-2',
  'eu-central-1',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-northeast-1',
];

function regionOptionsFor(currentRegion) {
  const r = String(currentRegion || '').trim();
  if (r && !DEFAULT_SES_REGIONS.includes(r)) {
    return [r, ...DEFAULT_SES_REGIONS];
  }
  return DEFAULT_SES_REGIONS;
}

const ReportEmailConfigAdmin = ({ open, getAuthHeaders, embedded = false }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [accessKeyId, setAccessKeyId] = useState('');
  const [accessKeySet, setAccessKeySet] = useState(false);
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [secretSet, setSecretSet] = useState(false);
  const [region, setRegion] = useState('us-east-1');
  const [from, setFrom] = useState('');
  const [configured, setConfigured] = useState(false);
  const [usesEnvFallback, setUsesEnvFallback] = useState(false);
  const [pdfRenderUrl, setPdfRenderUrl] = useState('');
  const [pdfRenderUrlSource, setPdfRenderUrlSource] = useState('');
  const [pdfCaptureWidth, setPdfCaptureWidth] = useState('1920');
  const [pdfCaptureHeight, setPdfCaptureHeight] = useState('1080');
  const [pdfCaptureLandscape, setPdfCaptureLandscape] = useState(true);
  const [pdfCaptureWidthEffective, setPdfCaptureWidthEffective] = useState(1920);
  const [pdfCaptureHeightEffective, setPdfCaptureHeightEffective] = useState(1080);
  const [pdfCaptureLandscapeEffective, setPdfCaptureLandscapeEffective] = useState(true);
  const [pdfCaptureSource, setPdfCaptureSource] = useState('default');
  const [testTo, setTestTo] = useState('');

  const regionOptions = useMemo(() => regionOptionsFor(region), [region]);

  const applyConfigFromResponse = (c) => {
    setAccessKeyId('');
    setSecretAccessKey('');
    setAccessKeySet(Boolean(c.accessKeySet));
    setSecretSet(Boolean(c.secretSet));
    setRegion(c.region || 'us-east-1');
    setFrom(c.from || '');
    setConfigured(Boolean(c.configured));
    setUsesEnvFallback(Boolean(c.usesEnvFallback));
    setPdfRenderUrl(c.pdfRenderUrl || '');
    setPdfRenderUrlSource(c.pdfRenderUrlSource || '');
    setPdfCaptureWidth(String(c.pdfCaptureWidth ?? 1920));
    setPdfCaptureHeight(String(c.pdfCaptureHeight ?? 1080));
    setPdfCaptureLandscape(c.pdfCaptureLandscape !== false);
    setPdfCaptureWidthEffective(c.pdfCaptureWidthEffective ?? 1920);
    setPdfCaptureHeightEffective(c.pdfCaptureHeightEffective ?? 1080);
    setPdfCaptureLandscapeEffective(c.pdfCaptureLandscapeEffective !== false);
    setPdfCaptureSource(c.pdfCaptureSource || 'default');
  };

  useEffect(() => {
    if (!open || !getAuthHeaders) return;
    setError(null);
    setLoading(true);
    const headers = getAuthHeaders();
    axios
      .get('/api/admin/report-email-config', { headers })
      .then((res) => {
        if (!res.data.success) {
          setError(res.data.error || 'Error al cargar');
          return;
        }
        const c = res.data.config || {};
        applyConfigFromResponse(c);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message || 'Error al cargar');
      })
      .finally(() => setLoading(false));
  }, [open, getAuthHeaders]);

  const handleSave = () => {
    const headers = getAuthHeaders();
    if (!headers) {
      showError('No hay credenciales de sesión');
      return;
    }
    setSaving(true);
    setError(null);
    axios
      .put(
        '/api/admin/report-email-config',
        {
          accessKeyId: accessKeyId.trim() || undefined,
          secretAccessKey: secretAccessKey.trim() || undefined,
          region: region.trim(),
          from: from.trim(),
          pdfCaptureWidth: parseInt(pdfCaptureWidth, 10) || 1920,
          pdfCaptureHeight: parseInt(pdfCaptureHeight, 10) || 1080,
          pdfCaptureLandscape,
        },
        { headers }
      )
      .then((res) => {
        if (!res.data.success) {
          setError(res.data.error || 'Error al guardar');
          return;
        }
        showSuccess('Configuración de reportes guardada');
        applyConfigFromResponse(res.data.config || {});
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message || 'Error al guardar';
        showError(msg);
        setError(msg);
      })
      .finally(() => setSaving(false));
  };

  const handleTest = () => {
    const headers = getAuthHeaders();
    if (!headers) {
      showError('No hay credenciales de sesión');
      return;
    }
    if (!testTo.trim()) {
      showWarning('Indica un correo para la prueba');
      return;
    }
    setTesting(true);
    setError(null);
    axios
      .post('/api/admin/report-email-config/test', { to: testTo.trim() }, { headers })
      .then((res) => {
        if (res.data.success) {
          showSuccess(`Correo de prueba enviado a ${res.data.to}`);
        } else {
          showError(res.data.error || 'No se pudo enviar la prueba');
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message || 'Error al enviar prueba';
        showError(msg);
        setError(msg);
      })
      .finally(() => setTesting(false));
  };

  const pdfRenderUrlSourceLabel = {
    env: 'variable PDF_RENDER_URL del .env',
    dev_auto: 'desarrollo (localhost + puerto del frontend)',
    docker: 'servicio frontend de docker-compose',
  }[pdfRenderUrlSource] || pdfRenderUrlSource;

  const pdfCaptureSourceLabel = {
    admin: 'guardada aquí (Admin)',
    env: 'variables REPORT_PDF_* del .env',
    default: 'valores por defecto (1920×1080, apaisado)',
  }[pdfCaptureSource] || pdfCaptureSource;

  const formContent = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configuración de reportes automáticos: generación del PDF (misma vista que la previsualización)
        y envío por correo con AWS SES. Se guarda en appdata (solo admin). Si no configuras aquí, el
        servidor puede usar variables del <code>.env</code> (<code>AWS_EMAIL_*</code>).
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Generación de PDF
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        El servidor renderiza los dashboards en un navegador interno con los datos del reporte y
        adjunta el PDF al correo. Ajusta el tamaño de captura para que coincida con tu pantalla
        (por defecto 1920×1080).
      </Typography>

      {pdfRenderUrl ? (
        <Alert severity="info" sx={{ mb: 3, maxWidth: 560 }}>
          <Typography variant="body2" component="div">
            Frontend interno: <code>{pdfRenderUrl}</code>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Origen: {pdfRenderUrlSourceLabel}
          </Typography>
        </Alert>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Tamaño de captura (viewport Puppeteer)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: 560, mb: 1 }}>
        <TextField
          label="Ancho (px)"
          type="number"
          value={pdfCaptureWidth}
          onChange={(e) => setPdfCaptureWidth(e.target.value)}
          inputProps={{ min: 800, max: 3840 }}
          sx={{ width: 160 }}
          helperText="800–3840"
        />
        <TextField
          label="Alto (px)"
          type="number"
          value={pdfCaptureHeight}
          onChange={(e) => setPdfCaptureHeight(e.target.value)}
          inputProps={{ min: 600, max: 2160 }}
          sx={{ width: 160 }}
          helperText="600–2160"
        />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={pdfCaptureLandscape}
            onChange={(e) => setPdfCaptureLandscape(e.target.checked)}
          />
        }
        label="PDF en A4 apaisado (recomendado para dashboards anchos)"
        sx={{ mb: 1, maxWidth: 560 }}
      />

      <Alert severity="success" sx={{ mb: 3, maxWidth: 560 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Captura activa
        </Typography>
        <Typography variant="body2" component="div">
          Viewport: <code>{pdfCaptureWidthEffective}×{pdfCaptureHeightEffective}</code>
          {' · '}
          PDF: A4 {pdfCaptureLandscapeEffective ? 'apaisado' : 'vertical'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Origen: {pdfCaptureSourceLabel}
        </Typography>
      </Alert>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Correo (AWS SES)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Credenciales para enviar el PDF por correo a los destinatarios del reporte.
      </Typography>

      {usesEnvFallback && !accessKeySet && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Actualmente el envío usa variables del archivo <code>.env</code>. Guarda aquí para
          centralizar la configuración sin reiniciar el servicio.
        </Alert>
      )}

      {configured && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Envío de reportes por correo configurado.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
          <TextField
            label="AWS Access Key ID"
            value={accessKeyId}
            onChange={(e) => setAccessKeyId(e.target.value)}
            fullWidth
            placeholder={accessKeySet ? '•••••••• (vacío = no cambiar)' : 'AKIA...'}
            helperText={accessKeySet ? 'Ya hay una clave guardada' : ''}
          />
          <TextField
            label="AWS Secret Access Key"
            type="password"
            value={secretAccessKey}
            onChange={(e) => setSecretAccessKey(e.target.value)}
            fullWidth
            placeholder={secretSet ? '•••••••• (vacío = no cambiar)' : ''}
            helperText={secretSet ? 'Ya hay un secret guardado' : ''}
          />
          <TextField
            select
            label="Región SES"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            fullWidth
            helperText="Debe coincidir con la región donde verificaste el dominio/correo en AWS SES"
          >
            {regionOptions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Remitente (From)"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            fullWidth
            placeholder="reportes@tudominio.com"
            helperText="Debe estar verificado en AWS SES"
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ pt: 1 }}>
            Probar envío
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Correo de prueba"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              sx={{ minWidth: 260, flex: 1 }}
            />
            <Button variant="outlined" onClick={handleTest} disabled={testing || !configured}>
              {testing ? 'Enviando…' : 'Enviar prueba'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );

  if (embedded) return formContent;
  return <Box sx={{ p: 2 }}>{formContent}</Box>;
};

export default ReportEmailConfigAdmin;
